import { useEffect, useMemo, useRef, useState } from "react";
import { api, money, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  X,
  Printer,
  EnvelopeSimple,
  ChatText,
  Tag,
  Star,
  PaperPlaneTilt,
  CaretLeft,
  Receipt,
  Money as MoneyIcon,
  CreditCard,
  Wallet,
  CheckCircle,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";

/**
 * SaleCompleteCard — the single unified "Sale Complete" surface.
 *
 * One stunning window, four payment-method skins.  Replaces the old
 * PostSaleActionsModal AND doubles as the SAVED state body inside
 * TyroPaymentDialog, so cashiers see the same gorgeous celebration
 * window whether they tapped Cash, Card, EFTPOS, or split-tender.
 *
 * Modes:
 *   • mode="overlay" (default) — full fixed-overlay modal with
 *     backdrop. Used standalone after non-Tyro sales (cash, manual
 *     card, other, split-without-Tyro).
 *   • mode="inline" — renders ONLY the action body (no overlay, no
 *     own card chrome, no hero header). Used embedded inside
 *     TyroPaymentDialog's existing card so the Tyro APPROVED screen
 *     smoothly morphs into the post-sale actions in the same window.
 *
 * The hero header in overlay mode adapts to `sale.payment_method`
 * (or the dominant `payments[]` method for split tenders).
 */
export default function SaleCompleteCard({
  open,
  onClose,
  sale,
  customer,
  store,
  repairTicket,
  onPrintReceipt,
  onPrintLabel,
  onViewInvoice,
  mode = "overlay",
  // "sale" (default) renders the standard blue header + green APPROVED
  // pill. "refund" flips the celebration to a red REFUNDED · $X.XX
  // returned pill, hides the Workshop & Follow-up workshop row (no
  // labels or Google review nudge for a refund), and the auto-close
  // copy / receipt labels read "refund receipt" instead of "receipt".
  kind = "sale",
  // Amount just requested via a Stripe payment link for this sale (e.g.
  // a $50 deposit on a $119 total) — NOT yet collected. The sale itself
  // only reflects what's actually been tendered in-store, so this is
  // passed in separately purely to label the celebration screen honestly
  // ("Deposit link sent · $50 requested" instead of implying the full
  // total was charged).
  stripeDepositAmount = 0,
}) {
  const [screen, setScreen] = useState("actions"); // actions | sms_receipt | email_receipt | sms_review
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  // 10-second auto-close. We use refs for the pause flag + a single
  // setInterval driven by `[open, sale.id]` so re-opening the card
  // for a new sale ALWAYS restarts a fresh 10s window — the previous
  // implementation kept stale `autoCloseLeft` state and slammed the
  // second sale's window shut on first paint because the timer effect
  // saw `autoCloseLeft <= 0` before the reset effect had a chance to
  // run.
  const [autoCloseLeft, setAutoCloseLeft] = useState(10);
  const [autoClosePaused, setAutoClosePaused] = useState(false);
  const pausedRef = useRef(false);
  useEffect(() => { pausedRef.current = autoClosePaused; }, [autoClosePaused]);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;
    setScreen("actions");
    setEmail(customer?.email || "");
    setPhone(customer?.phone || "");
    setAutoClosePaused(false);
    setAutoCloseLeft(10);
    let remaining = 10;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      remaining -= 1;
      setAutoCloseLeft(remaining);
      if (remaining <= 0) {
        clearInterval(id);
        onCloseRef.current?.();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [open, sale?.id, customer]);

  // Lock body scroll while the overlay is up — small but stops the
  // POS grid from twitching when the cashier's finger drags across
  // the card on a touchscreen.
  useEffect(() => {
    if (mode !== "overlay" || !open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mode, open]);

  // Pick the dominant payment method for theme + label. Multi-tender
  // sales (cash+card, card+Tyro, etc.) get a dedicated "Split" skin
  // so the hero clearly reads as a mixed transaction.
  const theme = useMemo(() => paymentTheme(sale, store, stripeDepositAmount), [sale, store, stripeDepositAmount]);

  if (!open || !sale) return null;

  const hasTicket = !!repairTicket;
  const hasReviewUrl = !!(store?.google_review_url || "").trim();
  const isRefund = kind === "refund";
  // Only what's actually been tendered in-store counts as "charged" —
  // a Stripe deposit link is a request, not money in hand yet. A repair
  // total of $119 with a $5 Stripe deposit sent must NOT read as
  // "$119.00 charged" — that's the exact bug this block fixes.
  const tenderedTotal = Number(sale.amount_tendered ?? 0);
  const saleTotalAmt = Number(sale.total ?? 0);
  const pendingStripe = Number(stripeDepositAmount || 0);
  // Mirrors the exact same math the checkout screen showed before this
  // sale was submitted (sum tendered INCLUDES the pending Stripe link,
  // since that money is "spoken for" even though it hasn't landed yet).
  const combinedTendered = tenderedTotal + pendingStripe;
  const balanceDue = Math.max(0, saleTotalAmt - combinedTendered);
  const isDeposit = !isRefund && balanceDue > 0.005;
  const depositCollectedInStore = tenderedTotal > 0.005;
  const pillAmount = isRefund
    ? saleTotalAmt
    : isDeposit
      ? (depositCollectedInStore ? tenderedTotal : pendingStripe)
      : saleTotalAmt;
  // Header gradient + APPROVED pill colours flip to red for refund
  // flows. Pill icon turns into an "arrow back" so the visual reads
  // "money going back to the customer" at a glance.
  const headerGradient = isRefund
    ? "linear-gradient(135deg, #DC2626 0%, #991B1B 50%, #450A0A 100%)"
    : "linear-gradient(135deg, #1E5FEB 0%, #1849C8 50%, #0F1F4E 100%)";
  const pillGradient = isRefund
    ? "linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)"
    : "linear-gradient(135deg, #10B981 0%, #047857 100%)";
  const pillBg = isRefund
    ? "linear-gradient(180deg, #FEF2F2 0%, #FFFFFF 100%)"
    : "linear-gradient(180deg, #ECFDF5 0%, #FFFFFF 100%)";
  const pillBorder = isRefund ? "#FECACA" : "#D1FAE5";
  const pillRingShadow = isRefund
    ? "0 4px 16px rgba(239,68,68,0.18)"
    : "0 4px 16px rgba(16,185,129,0.18)";
  const pillBorderRing = isRefund ? "#EF4444" : "#10B981";
  const pillTextColor = isRefund ? "#991B1B" : "#047857";
  const pillBoldColor = isRefund ? "#7F1D1D" : "#065F46";
  const pillAccentText = isRefund ? "#DC2626" : "#10B981";
  const pillVerb = isRefund
    ? "Refunded"
    : isDeposit
      ? (depositCollectedInStore ? "Deposit taken" : "Deposit link sent")
      : "Approved";
  const pillSuffix = isRefund
    ? "returned"
    : isDeposit
      ? (depositCollectedInStore ? "charged" : "requested")
      : "charged";
  const receiptKind = isRefund ? "refund receipt" : "receipt";

  const sendEmail = async () => {
    if (!email) return toast.error("Enter an email address");
    setSending(true);
    try {
      await api.post(`/sales/${sale.id}/email`, { to: email });
      toast.success(`Emailed receipt to ${email}`);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const sendReceiptSms = async () => {
    if (!phone) return toast.error("Enter a phone number (E.164, e.g. +15551234567)");
    setSending(true);
    try {
      await api.post(`/sales/${sale.id}/sms`, { to: phone });
      toast.success(`Texted receipt to ${phone}`);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const sendReviewSms = async () => {
    if (!phone) return toast.error("Enter a phone number");
    if (!hasReviewUrl) {
      return toast.error("No Google Review URL set for this store. Add it in Settings → Stores.");
    }
    setSending(true);
    try {
      await api.post(`/sales/${sale.id}/review-sms`, { to: phone });
      toast.success(`Review link sent to ${phone}`);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  // ─── Body content (shared by both modes) ───────────────────────
  const Body = (
    <>
      {screen === "actions" && (
        <div
          className="p-6 space-y-5 sale-complete-fade-in"
          data-testid="sale-complete-actions"
          onMouseEnter={() => setAutoClosePaused(true)}
          onMouseLeave={() => setAutoClosePaused(false)}
          onPointerDown={() => setAutoClosePaused(true)}
          onFocusCapture={() => setAutoClosePaused(true)}
        >
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-3 sale-complete-tile-stagger" style={{ "--d": "60ms" }}>
              Send receipt
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="sale-complete-tile-stagger" style={{ "--d": "120ms" }}>
                <ActionTile
                  testid="sale-complete-print-receipt"
                  icon={<Printer size={20} weight="bold" />}
                  label="Print"
                  sub={isRefund ? "Refund" : "Receipt"}
                  onClick={() => onPrintReceipt?.()}
                />
              </div>
              <div className="sale-complete-tile-stagger" style={{ "--d": "180ms" }}>
                <ActionTile
                  testid="sale-complete-sms-receipt"
                  icon={<ChatText size={20} weight="bold" />}
                  label="SMS"
                  sub={isRefund ? "Refund" : "Receipt"}
                  onClick={() => setScreen("sms_receipt")}
                />
              </div>
              <div className="sale-complete-tile-stagger" style={{ "--d": "240ms" }}>
                <ActionTile
                  testid="sale-complete-email-receipt"
                  icon={<EnvelopeSimple size={20} weight="bold" />}
                  label="Email"
                  sub={isRefund ? "Refund" : "Receipt"}
                  onClick={() => setScreen("email_receipt")}
                />
              </div>
            </div>
          </div>

          {!isRefund && (
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-3 sale-complete-tile-stagger" style={{ "--d": "300ms" }}>
              Workshop &amp; follow-up
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="sale-complete-tile-stagger" style={{ "--d": "340ms" }}>
                <ActionTile
                  testid="sale-complete-print-label"
                  icon={<Tag size={20} weight="bold" />}
                  label="Print label"
                  sub={hasTicket ? repairTicket.ticket_number : "Ticket only"}
                  onClick={() => onPrintLabel?.()}
                  disabled={!hasTicket}
                  disabledHint="Label only prints for sales linked to a repair ticket."
                />
              </div>
              <div className="sale-complete-tile-stagger" style={{ "--d": "400ms" }}>
                <ActionTile
                  testid="sale-complete-sms-review"
                  icon={<Star size={20} weight="bold" />}
                  label="SMS Google"
                  sub="5★ Review"
                  accent
                  onClick={() => setScreen("sms_review")}
                  disabled={!hasReviewUrl}
                  disabledHint="Add a Google Review URL for this store in Settings → Stores."
                />
              </div>
            </div>
          </div>
          )}

          <div className="flex gap-2 pt-1 sale-complete-tile-stagger" style={{ "--d": "470ms" }}>
            {onViewInvoice && (
              <button
                onClick={() => { onViewInvoice(); onClose?.(); }}
                data-testid="sale-complete-view-invoice"
                className="flex-1 h-12 rounded-full border border-[#E5E7EB] hover:border-[#121216] bg-white text-[#121216] text-[13px] font-semibold transition-colors active:scale-[0.98] flex items-center justify-center gap-1.5"
              >
                <Receipt size={14} weight="bold" /> View invoice
              </button>
            )}
            <button
              onClick={onClose}
              data-testid="sale-complete-done"
              className="flex-1 h-12 rounded-full bg-[#121216] hover:bg-[#1E5FEB] text-white text-[14px] font-semibold transition-colors active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <span>Done</span>
              {autoCloseLeft > 0 && (
                <span
                  className="inline-flex items-center justify-center text-[10.5px] font-mono tabular-nums font-bold rounded-full w-6 h-6 transition-opacity"
                  style={{
                    background: "rgba(255,255,255,0.18)",
                    opacity: autoClosePaused ? 0.4 : 1,
                  }}
                  title={autoClosePaused ? "Auto-close paused" : `Auto-closes in ${autoCloseLeft}s`}
                  data-testid="sale-complete-autoclose-countdown"
                >
                  {autoCloseLeft}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {screen === "sms_receipt" && (
        <InputScreen
          title="Text the receipt"
          hint="We'll text a short link to a web-viewable receipt with a Download PDF button."
          back={() => setScreen("actions")}
          action={sendReceiptSms}
          actionLabel={sending ? "Sending…" : "Send SMS"}
          sending={sending}
          accentColor={theme.accent}
        >
          <PhoneField value={phone} onChange={setPhone} testid="sale-complete-sms-phone" accentColor={theme.accent} />
        </InputScreen>
      )}

      {screen === "email_receipt" && (
        <InputScreen
          title="Email the receipt"
          hint="We'll email the PDF receipt plus a View Online link."
          back={() => setScreen("actions")}
          action={sendEmail}
          actionLabel={sending ? "Sending…" : "Send email"}
          sending={sending}
          accentColor={theme.accent}
        >
          <EmailField value={email} onChange={setEmail} testid="sale-complete-email-input" accentColor={theme.accent} />
        </InputScreen>
      )}

      {screen === "sms_review" && (
        <InputScreen
          title="Ask for a Google review"
          hint={
            <>
              Sends:{" "}
              <span className="text-[#121216]">
                &ldquo;Thanks for choosing {store?.name || "us"}! Loved your repair? Please leave us a 5-star review: <em>{store?.google_review_url}</em>&rdquo;
              </span>
            </>
          }
          back={() => setScreen("actions")}
          action={sendReviewSms}
          actionLabel={sending ? "Sending…" : "Send review link"}
          sending={sending}
          accentColor={theme.accent}
        >
          <PhoneField value={phone} onChange={setPhone} testid="sale-complete-review-phone" accentColor={theme.accent} />
        </InputScreen>
      )}
    </>
  );

  // Compact celebration banner — shared by inline + overlay. Color
  // and verb flip for refunds via the pill* variables computed at the
  // top of the component.
  const CelebrationBanner = (
    <div
      className="px-6 pt-6 pb-3 text-center sale-complete-celebration-bar"
      style={{
        background: pillBg,
        borderBottom: `1px solid ${pillBorder}`,
      }}
    >
      <div
        className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white"
        style={{ border: `1px solid ${pillBorderRing}`, boxShadow: pillRingShadow }}
      >
        <span
          className="w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0"
          style={{ background: pillGradient }}
        >
          {isRefund ? <ArrowCounterClockwise size={14} weight="bold" /> : <CheckCircle size={14} weight="fill" />}
        </span>
        <span className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: pillTextColor }}>
          {pillVerb}
        </span>
        <span className="text-[12px] text-[#9CA3AF]" aria-hidden>·</span>
        <span className="font-mono tabular-nums text-[14px] font-bold" style={{ color: pillBoldColor }}>
          ${money(pillAmount)}
        </span>
        <span className="text-[11px] font-semibold" style={{ color: pillAccentText }}>
          {pillSuffix}
        </span>
      </div>
      <div className="mt-1.5 text-[11.5px] text-[#6B7280]">
        Sale {sale.sale_number ? <span className="font-mono font-semibold" style={{ color: pillBoldColor }}>{sale.sale_number}</span> : "saved"} · what would you like to do next?
      </div>
      {isDeposit && (
        <div className="mt-1.5 text-[11.5px] font-semibold" style={{ color: "#DC2626" }} data-testid="sale-complete-balance-due">
          Balance due: ${money(balanceDue)} — customer can pay later from the invoice
        </div>
      )}
    </div>
  );

  // ─── Inline mode — return banner + action body ─────────────────
  if (mode === "inline") {
    return (
      <div className="w-full" data-testid="sale-complete-inline">
        {CelebrationBanner}
        {Body}
      </div>
    );
  }

  // ─── Overlay mode — full modal with adaptive hero ──────────────
  return (
    <div
      className="fixed inset-0 z-[10020] bg-black/55 backdrop-blur-md flex items-center justify-center p-4 sale-complete-overlay-fade"
      onClick={onClose}
      data-testid="sale-complete-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg rounded-3xl overflow-hidden sale-complete-card-pop"
        style={{ boxShadow: theme.shadow }}
        role="dialog"
        aria-labelledby="sale-complete-title"
        aria-modal="true"
      >
        {/* Hero header — uses the same POS blue across all payment
            methods so the Sale Complete card visually matches the
            POS chrome. The green stays exclusively on the APPROVED
            pill below, where it carries the "success" meaning. */}
        <div
          className="relative px-6 pt-6 pb-5 text-white overflow-hidden"
          style={{ background: headerGradient }}
        >
          {/* One-shot diagonal sheen sweep — sub-conscious polish that
              tells the cashier's eye "this card is alive". Triggers
              exactly once per open via the key on the parent + the
              animation-fill-mode: forwards on the keyframe. */}
          <div aria-hidden className="sale-complete-sheen" />
          {/* Subtle bokeh grain so the gradient doesn't feel flat */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.18] pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.4), transparent 45%), radial-gradient(circle at 90% 100%, rgba(255,255,255,0.25), transparent 50%)",
            }}
          />
          <div className="relative flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 sale-complete-icon-pop"
              style={{
                background: "rgba(255,255,255,0.18)",
                backdropFilter: "blur(14px)",
                border: "1px solid rgba(255,255,255,0.28)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.3)",
              }}
            >
              {theme.icon}
            </div>
            <div className="min-w-0">
              <div
                id="sale-complete-title"
                className="font-display font-semibold text-[17px] leading-tight tracking-tight"
              >
                {theme.label}
              </div>
              <div className="text-[10.5px] mt-1 font-mono uppercase tracking-[0.14em] opacity-75 truncate">
                {theme.subline}
              </div>
            </div>
          </div>
          {/* Tender breakdown chips (only meaningful on split tenders) */}
          {theme.chips.length > 0 && (
            <div className="relative flex flex-wrap gap-1.5 mt-3">
              {theme.chips.map((c, i) => (
                <span
                  key={i}
                  className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.14em] font-bold"
                  style={{
                    background: "rgba(255,255,255,0.16)",
                    border: "1px solid rgba(255,255,255,0.28)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Approved / Refunded banner — shared between overlay + inline */}
        {CelebrationBanner}

        {Body}
      </div>

      {/* Inline keyframes so the card has zero-config motion. Kept
          here so SaleCompleteCard remains a single-file drop-in. */}
      <style>{`
        @keyframes sale-complete-overlay-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .sale-complete-overlay-fade {
          animation: sale-complete-overlay-fade 180ms ease-out both;
        }
        @keyframes sale-complete-sheen {
          0%   { transform: translateX(-120%) skewX(-22deg); opacity: 0; }
          18%  { opacity: 0.9; }
          100% { transform: translateX(220%) skewX(-22deg); opacity: 0; }
        }
        .sale-complete-sheen {
          position: absolute;
          top: 0;
          left: 0;
          width: 38%;
          height: 100%;
          pointer-events: none;
          background: linear-gradient(
            100deg,
            rgba(255,255,255,0)   0%,
            rgba(255,255,255,0.18) 45%,
            rgba(255,255,255,0.38) 50%,
            rgba(255,255,255,0.18) 55%,
            rgba(255,255,255,0)   100%
          );
          mix-blend-mode: screen;
          animation: sale-complete-sheen 1200ms cubic-bezier(.22,1,.36,1) 280ms both;
          will-change: transform, opacity;
        }
        @keyframes sale-complete-card-pop {
          0%   { opacity: 0; transform: translateY(14px) scale(0.96); }
          60%  { opacity: 1; transform: translateY(-2px) scale(1.01); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .sale-complete-card-pop {
          animation: sale-complete-card-pop 360ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @keyframes sale-complete-icon-pop {
          0%   { transform: scale(0.6) rotate(-8deg); opacity: 0; }
          70%  { transform: scale(1.08) rotate(2deg); opacity: 1; }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        .sale-complete-icon-pop { animation: sale-complete-icon-pop 480ms cubic-bezier(0.22, 1, 0.36, 1) 80ms both; }
        @keyframes sale-complete-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .sale-complete-fade-in {
          animation: sale-complete-fade-in 320ms cubic-bezier(0.22, 1, 0.36, 1) 120ms both;
        }
        @keyframes sale-complete-tile-pop {
          0%   { opacity: 0; transform: translateY(8px) scale(0.96); }
          60%  { opacity: 1; transform: translateY(-1px) scale(1.01); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .sale-complete-tile-stagger {
          animation: sale-complete-tile-pop 380ms cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: var(--d, 0ms);
          will-change: transform, opacity;
        }
        @media (prefers-reduced-motion: reduce) {
          .sale-complete-tile-stagger,
          .sale-complete-fade-in,
          .sale-complete-card-pop,
          .sale-complete-icon-pop,
          .sale-complete-overlay-fade { animation-duration: 80ms !important; animation-delay: 0ms !important; }
        }
      `}</style>
    </div>
  );
}

/* ───────────────────────────── helpers ───────────────────────── */

function paymentTheme(sale, _store, stripeDepositAmount = 0) {
  const payments = Array.isArray(sale?.payments) ? sale.payments : [];
  const distinctMethods = [...new Set(payments.map((p) => (p.method || "").toLowerCase()).filter(Boolean))];
  const primary = (sale?.payment_method || payments[0]?.method || "cash").toLowerCase();
  const tenderedTotal = Number(sale?.amount_tendered ?? 0);
  const hasStripePending = Number(stripeDepositAmount || 0) > 0;

  // Tyro detection — split-tender Tyro slices carry `tyro_meta`, and
  // single-tender Tyro sales store `card_manual` with top-level
  // `tyro_transaction_reference`. Either way we want the green skin.
  const hasTyro =
    primary === "tyro" ||
    !!(sale?.tyro_transaction_reference || sale?.tyro_authorisation_code) ||
    payments.some((p) => p?.tyro_meta);

  // Build pretty chips for split tenders (always show when > 1 method,
  // or whenever a Stripe deposit link is pending alongside in-store tender)
  const chips =
    distinctMethods.length > 1 || (hasStripePending && distinctMethods.length >= 1)
      ? payments.map((p) => {
          const m = (p.method || "").toLowerCase();
          const isTyro = m === "tyro" || (m === "card_manual" && p?.tyro_meta);
          const label = isTyro ? "Tyro" : m === "card_manual" ? "Card" : (m || "Other");
          return `${label.toUpperCase()} · $${money(p.amount || 0)}`;
        })
      : [];
  if (hasStripePending) chips.push(`STRIPE LINK · $${money(stripeDepositAmount)}`);

  // All payment methods share the SAME blue POS hero gradient so cash,
  // card, split and Tyro all feel like one consistent celebration UI
  // that matches the POS chrome. The green stays only on the APPROVED
  // pill, where it carries the "success" meaning.
  const BLUE_SHADOW = "0 24px 64px rgba(30, 95, 235, 0.32)";
  const BLUE_ACCENT = "#1E5FEB";

  // Nothing collected in-store at all — the whole sale is riding on a
  // pending Stripe payment link. Label this honestly instead of falling
  // through to the "Cash · Counter sale" default.
  if (tenderedTotal <= 0.005 && hasStripePending && distinctMethods.length === 0) {
    return {
      label: "Stripe deposit",
      subline: "Payment link sent to customer",
      shadow: BLUE_SHADOW,
      accent: BLUE_ACCENT,
      icon: <PaperPlaneTilt size={20} weight="fill" />,
      chips,
    };
  }

  if (distinctMethods.length > 1 || hasStripePending) {
    return {
      label: "Split tender",
      subline: chips.join("  ·  "),
      shadow: BLUE_SHADOW,
      accent: BLUE_ACCENT,
      icon: <Wallet size={22} weight="fill" />,
      chips,
    };
  }

  if (hasTyro) {
    return {
      label: "Tyro EFTPOS",
      subline: "Approved at terminal",
      shadow: BLUE_SHADOW,
      accent: BLUE_ACCENT,
      icon: <CreditCard size={20} weight="fill" />,
      chips: [],
    };
  }

  if (primary === "cash") {
    return {
      label: "Cash",
      subline: "Counter sale",
      shadow: BLUE_SHADOW,
      accent: BLUE_ACCENT,
      icon: <MoneyIcon size={20} weight="fill" />,
      chips: [],
    };
  }

  if (primary === "card_manual") {
    return {
      label: "Card",
      subline: "Manual entry",
      shadow: BLUE_SHADOW,
      accent: BLUE_ACCENT,
      icon: <CreditCard size={20} weight="fill" />,
      chips: [],
    };
  }

  // Fallback: "other" or unknown
  return {
    label: primary.replace(/_/g, " ").toUpperCase() || "PAID",
    subline: "Counter sale",
    shadow: BLUE_SHADOW,
    accent: BLUE_ACCENT,
    icon: <CheckCircle size={20} weight="fill" />,
    chips: [],
  };
}

function ActionTile({ icon, label, sub, onClick, disabled, disabledHint, accent, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? disabledHint : undefined}
      data-testid={testid}
      className={`group relative flex items-center gap-3 py-3.5 px-3.5 rounded-2xl border transition-all text-left w-full ${
        disabled
          ? "border-[#F3F4F6] bg-[#F8FAFC] text-[#9CA3AF] cursor-not-allowed"
          : accent
          ? "border-[#1E5FEB] bg-[#DBEAFE] hover:bg-[#1E5FEB] hover:text-white text-[#1E5FEB] active:scale-[0.98]"
          : "border-[#E5E7EB] hover:border-[#121216] hover:bg-[#F8FAFC] text-[#121216] active:scale-[0.98]"
      }`}
    >
      <div className="shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold leading-tight truncate">{label}</div>
        <div className="text-[11px] opacity-70 leading-tight mt-0.5 truncate">{sub}</div>
      </div>
    </button>
  );
}

function InputScreen({ title, hint, back, action, actionLabel, sending, children, accentColor }) {
  return (
    <div className="p-6 space-y-4 sale-complete-fade-in">
      <button
        onClick={back}
        className="inline-flex items-center gap-1 text-[12px] text-[#4B5563] hover:text-[#121216]"
        data-testid="sale-complete-back"
      >
        <CaretLeft size={12} weight="bold" /> Back
      </button>
      <div>
        <div className="font-display text-[22px] font-semibold tracking-tight text-[#121216]">
          {title}
        </div>
        {hint && <div className="text-[12px] text-[#4B5563] leading-relaxed mt-1.5">{hint}</div>}
      </div>
      {children}
      <button
        onClick={action}
        disabled={sending}
        data-testid="sale-complete-send"
        className="w-full h-12 rounded-full disabled:bg-[#9CA3AF] text-white text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
        style={{ background: sending ? "#9CA3AF" : (accentColor || "#1E5FEB") }}
      >
        <PaperPlaneTilt size={14} weight="bold" />
        {actionLabel}
      </button>
    </div>
  );
}

function PhoneField({ value, onChange, testid, accentColor }) {
  const ring = (accentColor || "#1E5FEB") + "26";
  return (
    <label className="block">
      <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-2">
        Phone (E.164)
      </div>
      <input
        type="tel"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="+15551234567"
        data-testid={testid}
        className="w-full h-12 px-4 rounded-xl border border-[#E5E7EB] focus:outline-none text-[14px] font-mono bg-white transition-all"
        style={{ "--ring": ring }}
        onFocus={(e) => { e.target.style.borderColor = accentColor || "#1E5FEB"; e.target.style.boxShadow = `0 0 0 4px ${ring}`; }}
        onBlur={(e) => { e.target.style.borderColor = "#E5E7EB"; e.target.style.boxShadow = "none"; }}
      />
    </label>
  );
}

function EmailField({ value, onChange, testid, accentColor }) {
  const ring = (accentColor || "#1E5FEB") + "26";
  return (
    <label className="block">
      <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-2">
        Email
      </div>
      <input
        type="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="customer@example.com"
        data-testid={testid}
        className="w-full h-12 px-4 rounded-xl border border-[#E5E7EB] focus:outline-none text-[14px] bg-white transition-all"
        onFocus={(e) => { e.target.style.borderColor = accentColor || "#1E5FEB"; e.target.style.boxShadow = `0 0 0 4px ${ring}`; }}
        onBlur={(e) => { e.target.style.borderColor = "#E5E7EB"; e.target.style.boxShadow = "none"; }}
      />
    </label>
  );
}
